import React, { useMemo } from "react";
import { Table } from "../../../../../shared/Table/Table";
import classes from "../../../newBusinessInsightsContent.module.scss";
import { useTotalDiscountSummary } from "./hooks/useTotalDiscountSummary";
import Header from "../../../components/Header";
import { useHeaderActions } from "../../../hooks/useHeaderActions";
import { Box } from "../../../../../shared/Box/Box";
import { Skeleton } from "../../../../../shared/Skeleton/Skeleton";
import { Empty } from "../../../../../shared/Empty/Empty";
import { HTTP_ENDPOINTS } from "../../../../../consts/api";

const modalInfo = {
  title: "totalDiscountSummaryReport.info.title",
  rows: [
    "totalDiscountSummaryReport.info.clinicSummary",
    "totalDiscountSummaryReport.info.providerSummary",
  ],
};

const TotalDiscountSummary = () => {
  const { selectedClinic, period, apiPeriod } = useHeaderActions();
  const requestParams = {
    fromDate: apiPeriod.fromDate,
    toDate: apiPeriod.toDate,
    clinic_id:
      selectedClinic.value.value !== 0 ? [selectedClinic.value.value] : [],
  };

  const { data, isLoading } = useTotalDiscountSummary(requestParams);

  const isClinicDataEmpty = !isLoading && !data?.clinicTotalDiscounts?.length;
  const isProviderDataEmpty =
    !isLoading && !data?.providerTotalDiscounts?.length;

  const clinicData = useMemo(
    () =>
      data?.clinicTotalDiscounts?.map((clinic) => ({
        clinicName: clinic.clinic_name,
        itemLineDiscounts: clinic.total_item_discount,
        customDiscounts: clinic.total_custom_discount,
        packageDiscounts: clinic.total_package_discount,
        mfgDiscounts: clinic.total_mfg_discount,
        walletDebitShare: clinic.total_wallet_debit_share,
        totalDiscounts: clinic.total_discount_amount,
      })) || [],
    [data],
  );

  const providerData = useMemo(
    () =>
      data?.providerTotalDiscounts?.map((provider) => ({
        providerName: provider.provider_name,
        itemLineDiscounts: provider.total_item_discount,
        customDiscounts: provider.total_custom_discount,
        packageDiscounts: provider.total_package_discount,
        mfgDiscounts: provider.total_mfg_discount,
        walletDebitShare: provider.total_wallet_debit_share,
        totalDiscounts: provider.total_discount_amount,
      })) || [],
    [data],
  );

  const tableColumns = [
    { data: "Item Discounts", accessor: "itemLineDiscounts" },
    { data: "Custom Discounts", accessor: "customDiscounts" },
    { data: "Package Discounts", accessor: "packageDiscounts" },
    { data: "Manufacturer Discounts", accessor: "mfgDiscounts" },
    { data: "Wallet Debit Share", accessor: "walletDebitShare" },
    { data: "Total Discounts", accessor: "totalDiscounts" },
  ];

  return (
    <div className="flex-col font-14 gap-16">
      <Header
        title="Total Discount Summary Report"
        selectedClinic={selectedClinic}
        period={period}
        downloadRoute={HTTP_ENDPOINTS.getTotalDiscountSummary()}
        requestParams={requestParams}
        modalInfo={modalInfo}
      />
      <div className="flex-col gap-16">
        <div className={classes.tableContainer}>
          <Table
            bgColor="white"
            cols={[
              { data: "Clinic Name", accessor: "clinicName" },
              ...tableColumns,
            ]}
            data={clinicData}
          />
          {isLoading && (
            <Box>
              <Skeleton count={8} height={40.5} borderRadius="0px" />
            </Box>
          )}
          {isClinicDataEmpty && (
            <Box>
              <Empty position="center" />
            </Box>
          )}
        </div>
        <div className={classes.tableContainer}>
          <Table
            bgColor="white"
            cols={[
              { data: "Provider Name", accessor: "providerName" },
              ...tableColumns,
            ]}
            data={providerData}
          />
          {isLoading && (
            <Box>
              <Skeleton count={8} height={40.5} borderRadius="0px" />
            </Box>
          )}
          {isProviderDataEmpty && (
            <Box>
              <Empty position="center" />
            </Box>
          )}
        </div>
      </div>
    </div>
  );
};

export default TotalDiscountSummary;
